import safeStorage from '@github-ui/safe-storage'
import authToken from './auth-token'
import type {PlaygroundAPIMessage, PlaygroundMessage, PlaygroundRequestParameters, TextInputs, ToolCall} from '../types'
import type {Model} from '@github-ui/marketplace-common'
import {MessageStreamer} from './message-streamer'
import {
  TooManyRequestsError,
  PlaygroundChatRequestSent,
  PlaygroundChatRequestStreamingStarted,
  PlaygroundChatRequestStreamingCompleted,
  PlaygroundChatRateLimited,
  ContentErrorResponseError,
  TokenLimitReachedResponseError,
  TimeoutError,
} from './playground-types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {sendStats} from '@github-ui/stats'
import {searchDocuments} from './rag-index-manager'

type SendMessageCallback = (message: PlaygroundMessage) => void

export interface ModelClient {
  sendMessage(
    panel: number,
    model: Model,
    messages: PlaygroundMessage[],
    parameters: PlaygroundRequestParameters,
    systemPrompt: string | null,
    callback: SendMessageCallback,
  ): Promise<boolean>
}

export class AzureModelClient implements ModelClient {
  url: string
  timeoutInMs: number = 120000 // 2 mins
  // Has to be an object as in comparison mode a Panel can trigger messages for another Panel
  messageStreamers: {[key: string]: MessageStreamer} = {}

  constructor(url: string) {
    this.url = url
  }

  async sendMessage(
    panel: number,
    model: Model,
    messages: PlaygroundMessage[],
    parameters: PlaygroundRequestParameters,
    systemPrompt: string | null,
    callback: SendMessageCallback,
  ): Promise<boolean> {
    let uuid = null
    if (crypto && typeof crypto.randomUUID === 'function') {
      uuid = crypto.randomUUID()
    }

    const res = await this.makeRequest(model, messages, parameters, systemPrompt, uuid)
    this.emitChatRequestSent(res, model)
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.error('Failed to send chat request with ID', uuid, res)
      switch (res.status) {
        case 413:
          throw new TokenLimitReachedResponseError()

        case 429: {
          this.emitRateLimitedEvent(res, model)
          const timeLeft = res.headers.get('retry-after')
          const errorMessage = timeLeft
            ? `Rate limited, please try again in ${timeLeft} seconds.`
            : 'Rate limited, please try again later.'
          throw new TooManyRequestsError(errorMessage)
        }
        default: {
          // No special handling, lets check if the response is JSON and has more info

          let errorMessage = null
          try {
            const json = await res.json()
            errorMessage = json?.error?.message
          } catch {
            // Response was no JSON, so show a generic error
            throw new Error('An error occurred while processing your request.')
          }
          if (typeof errorMessage === 'string') {
            throw new ContentErrorResponseError(errorMessage)
          } else {
            throw new Error('An error occurred while processing your request.')
          }
        }
      }
    }

    this.emitStreamingStarted(model)
    let fullMessage = ''

    if (this.supportsStreaming(model)) {
      const reader = res.body?.getReader()
      if (!reader) {
        return false
      }

      // Stop any existing streaming for this panel
      this.stopStreamingMessages(panel)
      this.messageStreamers[panel] = new MessageStreamer(reader)

      // Keep track of tool calls, since the arguments are split up
      // in multiple chunks.
      const toolCalls: ToolCall[] = []

      for await (const chunk of this.messageStreamers[panel].stream()) {
        if (chunk.choices.length === 0) {
          continue
        }

        // Loop through each tool call in this chunk and
        // merge the arguments into the tracking array.
        for (const toolCall of chunk.choices[0]?.delta?.tool_calls ?? []) {
          // The first chunk for a tool call will have the name,
          // subsequent chunks will have the arguments.
          if (toolCall.function.name) {
            toolCalls.push(toolCall)
            continue
          }
          // toolCall.index can be 0, so we need to check if the key exists
          if ('index' in toolCall) {
            ;(toolCalls[toolCall.index] as ToolCall).function.arguments += toolCall.function.arguments
            continue
          }
        }

        if (chunk.choices[0]?.finish_reason === 'tool_calls') {
          const toolToCall = toolCalls[chunk.choices[0]?.index] as ToolCall

          let functionResponse = ''
          switch (toolToCall?.function.name) {
            case 'search_documents': {
              const searchArgs = JSON.parse(toolToCall.function.arguments)
              messages.push({
                timestamp: new Date(),
                role: 'assistant',
                message: [
                  {type: 'tool_calls', tool_calls: [toolToCall]},
                  {type: 'text', text: `Searching documents in index for "${searchArgs.query}"...`},
                ],
              })
              try {
                functionResponse = await searchDocuments({
                  query: searchArgs.query,
                  endpoint: this.localStorage.getItem('ragIndexEndpoint')!,
                  apiKey: this.localStorage.getItem('ragIndexAPIKey')!, // (await this.getAuthToken()).token
                })
              } catch (err: Error | unknown) {
                functionResponse = `Error searching documents: ${err}`
              }
            }
          }

          messages.push({
            timestamp: new Date(),
            role: 'tool',
            tool_call_id: toolToCall.id,
            message: functionResponse,
          })

          return this.sendMessage(panel, model, messages, parameters, systemPrompt, callback)
        }

        if (!chunk.choices[0]?.delta?.content) {
          continue
        }
        fullMessage += chunk.choices[0]?.delta?.content
        callback({
          timestamp: new Date(),
          role: 'assistant',
          message: fullMessage,
        })
      }
    } else {
      const response = await res.json()
      if (response.choices.length > 0 && response.choices[0]?.message?.content) {
        fullMessage = response.choices[0].message.content
        callback({
          timestamp: new Date(),
          role: 'assistant',
          message: fullMessage,
        })
      }
    }

    this.emitStreamingCompleted(model)
    return true
  }

  stopStreamingMessages = (position: number) => {
    const messageStreamer = this.messageStreamers[position]
    messageStreamer?.stop()
  }

  private async makeRequest(
    model: Model,
    messages: PlaygroundMessage[],
    parameters: PlaygroundRequestParameters,
    systemPrompt: string | null,
    uuid: string | null,
  ) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-ms-model-mesh-model-name': model.original_name.toLowerCase(),
      'x-ms-useragent': 'github-models-playground',
      'x-ms-user-agent': 'github-models-playground', // send both to accommodate various Azure consumers
      Authorization: await authToken.getAuthTokenValue(),
    }

    if (uuid) {
      headers['x-ms-client-request-id'] = uuid
    }

    const newMessages: PlaygroundAPIMessage[] = []
    if (systemPrompt) {
      newMessages.push({role: 'system', content: systemPrompt})
    }

    const existingMessagesAsPayload: PlaygroundAPIMessage[] = messages
      // Dont send error messages back to the API
      .filter(m => m.role !== 'error')
      .map(m => {
        // If the message is an array with a text type, convert it
        // to an assistant message where content is the text.
        if (Array.isArray(m.message) && m.message[0]!.type === 'tool_calls') {
          return {
            role: m.role,
            content: (m.message[1] as TextInputs).text,
            tool_calls: m.message[0]!.tool_calls,
          }
        }
        // If it's a tool message, ensure the `tool_call_id` is attached.
        if (m.role === 'tool') {
          return {
            role: m.role,
            content: m.message,
            tool_call_id: m.tool_call_id,
          }
        }
        return {role: m.role, content: m.message}
      })

    for (const m of existingMessagesAsPayload) {
      if (typeof m.content === 'string') {
        if (m.content.trim() !== '') {
          newMessages.push(m)
        }
      } else {
        newMessages.push(m)
      }
    }

    const body: {stream?: boolean; messages: PlaygroundAPIMessage[]} = {
      ...parameters,
      messages: newMessages,
    }

    // o1 models do not support streaming
    if (this.supportsStreaming(model)) {
      body.stream = true
    }

    const controller = new AbortController()
    setTimeout(() => controller.abort(new TimeoutError('Sorry, this is taking longer than usual.')), this.timeoutInMs)
    return fetch(this.url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    })
  }

  private localStorage = safeStorage('localStorage', {
    throwQuotaErrorsOnSet: false,
    ttl: 1000 * 60 * 60 * 24,
  })

  private emitChatRequestSent(res: Response, model: Model) {
    sendEvent(PlaygroundChatRequestSent, {
      registry: model.registry,
      model: model.name,
      publisher: model.publisher,
      success: res.ok,
      result_code: res.status,
    })

    if (res.ok) {
      sendStats({incrementKey: 'MODELS_CHAT_REQUEST', requestUrl: window.location.href})
    } else {
      sendStats({incrementKey: 'MODELS_CHAT_REQUEST_ERROR', requestUrl: window.location.href})
    }
  }

  private emitRateLimitedEvent(res: Response, model: Model) {
    sendEvent(PlaygroundChatRateLimited, {
      registry: model.registry,
      model: model.name,
      publisher: model.publisher,
      rate_limit_type: res.headers.get('X-RateLimit-Type'),
    })
  }

  private emitStreamingStarted(model: Model) {
    sendEvent(PlaygroundChatRequestStreamingStarted, {
      registry: model.registry,
      model: model.name,
      publisher: model.publisher,
    })
  }

  private emitStreamingCompleted(model: Model) {
    sendEvent(PlaygroundChatRequestStreamingCompleted, {
      registry: model.registry,
      model: model.name,
      publisher: model.publisher,
    })
  }

  private supportsStreaming(model: Model) {
    return model.name !== 'o1-mini' && model.name !== 'o1-preview'
  }
}
